import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, RefreshCw, Trophy, Shield, Clock, Brain, Cpu, GitCompare } from 'lucide-react';
import { apiGet, apiPost } from '../data/api';

const MARKETS = [
  { key: 'all', label: 'Tutti' },
  { key: 'match_winner', label: 'MW' },
  { key: 'handicap', label: 'HC' },
  { key: 'over_under', label: 'O/U' },
];

// SetScore engine uses slightly different market keys. Centralized here so
// the FE can switch endpoint while keeping the same UI tab control.
const SETSCORE_MARKET_MAP = {
  all: 'all',
  match_winner: 'match_winner',
  handicap: 'handicap',
  over_under: 'totals',
};

const ENGINES = [
  { key: 'legacy', label: 'AI Legacy', icon: Cpu },
  { key: 'setscore', label: 'SetScore (paper)', icon: Brain },
  { key: 'compare', label: 'Compare', icon: GitCompare },
];

function MarketBadge({ type }) {
  const styles = {
    match_winner: 'bg-blue-500/15 text-blue-400',
    handicap: 'bg-amber-500/15 text-amber-400',
    over_under: 'bg-purple-500/15 text-purple-400',
    totals: 'bg-purple-500/15 text-purple-400',
  };
  const labels = {
    match_winner: 'MW',
    handicap: 'HC',
    over_under: 'O/U',
    totals: 'O/U',
  };
  return (
    <span data-testid={`market-badge-${type}`} className={`px-2 py-0.5 text-xs font-medium rounded ${styles[type] || 'bg-zinc-700 text-zinc-400'}`}>
      {labels[type] || type}
    </span>
  );
}

function PaperBadge({ settleable }) {
  // SetScore picks are paper-only. HC -1.5 / OU are surfaced for audit
  // but the settler only resolves match_winner today, so we tag them
  // explicitly so the user knows they cannot be auto-graded yet.
  return (
    <span
      data-testid="setscore-paper-badge"
      title={settleable
        ? 'SetScore Paper — settleable on match end'
        : 'SetScore Paper — non-settleable yet (settler resolves only match_winner)'}
      className={`inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded border ${
        settleable
          ? 'bg-violet-500/15 text-violet-300 border-violet-500/40'
          : 'bg-zinc-800 text-zinc-400 border-zinc-700'
      }`}
    >
      {settleable ? 'paper' : 'paper · n/s'}
    </span>
  );
}

function ConfidenceBadge({ level }) {
  const map = {
    molto_alta: { label: 'Molto Alta', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
    alta:       { label: 'Alta',        cls: 'bg-teal-500/15 text-teal-400 border-teal-500/30' },
    media:      { label: 'Media',       cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
    bassa:      { label: 'Bassa',       cls: 'bg-red-500/15 text-red-400 border-red-500/30' },
  };
  const cfg = map[level] || map.bassa;
  return (
    <span
      data-testid={`confidence-badge-${level || 'bassa'}`}
      className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded border ${cfg.cls} uppercase tracking-wider`}
    >
      {cfg.label}
    </span>
  );
}

function SafetyMeter({ prob }) {
  const pct = Math.min(prob * 100, 100);
  const color = pct >= 75 ? '#10B981' : pct >= 60 ? '#F59E0B' : '#EF4444';
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-1.5 rounded-full bg-zinc-800">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="text-xs tabular-nums" style={{ color, fontFamily: 'JetBrains Mono, monospace' }}>
        {pct.toFixed(1)}%
      </span>
    </div>
  );
}

function formatTime(dt) {
  if (!dt) return '--:--';
  try {
    const d = new Date(dt);
    return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  } catch { return '--:--'; }
}

function formatDate(dt) {
  if (!dt) return '';
  try {
    const d = new Date(dt);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
  } catch { return ''; }
}

export default function TopPicks() {
  const [picks, setPicks] = useState([]);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [market, setMarket] = useState('all');
  const [engine, setEngine] = useState('legacy');

  // Compare-mode datasets — populated only when engine='compare'.
  const [compareLegacy, setCompareLegacy] = useState([]);
  const [compareSetscore, setCompareSetscore] = useState([]);

  const fetchPicks = async (mkt, eng) => {
    try {
      if (eng === 'compare') {
        const ssMarket = SETSCORE_MARKET_MAP[mkt] || 'all';
        const [legacyData, setscoreData] = await Promise.all([
          apiGet(`/tt/top-picks?market=${mkt}&limit=20`).catch(() => ({ picks: [] })),
          apiGet(`/tt/setscore/top-picks?market=${ssMarket}&limit=20&min_ev_pct=5`)
            .catch(() => ({ picks: [] })),
        ]);
        setCompareLegacy(legacyData.picks || []);
        setCompareSetscore(setscoreData.picks || []);
        setPicks([]);
        setTotalAvailable(
          (legacyData.picks || []).length + (setscoreData.picks || []).length,
        );
        return;
      }
      let data;
      if (eng === 'setscore') {
        const ssMarket = SETSCORE_MARKET_MAP[mkt] || 'all';
        data = await apiGet(
          `/tt/setscore/top-picks?market=${ssMarket}&limit=20&min_ev_pct=5`,
        );
      } else {
        data = await apiGet(`/tt/top-picks?market=${mkt}&limit=20`);
      }
      setPicks(data.picks || []);
      setTotalAvailable(data.total_available || 0);
    } catch (e) {
      console.error(e);
      setPicks([]);
      setTotalAvailable(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchPicks(market, engine);
  }, [market, engine]);

  const handleRefresh = async () => {
    setRefreshing(true);
    if (engine === 'legacy') {
      // Only legacy has a side-effectful regenerate job. SetScore is a
      // pure read-through view of `tt_setscore_predictions` + book odds.
      try {
        await apiPost('/tt/admin/generate-recommendations', {});
      } catch {}
    }
    await fetchPicks(market, engine);
    setRefreshing(false);
  };

  const topThree = useMemo(() => picks.slice(0, 3), [picks]);
  const rest = useMemo(() => picks.slice(3), [picks]);

  return (
    <div data-testid="top-picks-page" className="space-y-6 max-w-[1100px] fade-in">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Top Picks
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {engine === 'setscore'
              ? 'Generative SetScore Engine — paper-mode read-only ranking'
              : engine === 'compare'
              ? 'Confronto AI Legacy vs SetScore — consensus & divergenze per match'
              : 'Giocate ordinate per sicurezza del modello'}
          </p>
        </div>
        <button
          data-testid="refresh-picks-btn"
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-colors disabled:opacity-50"
          style={{ background: '#10B981', color: '#000' }}
        >
          {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          {refreshing ? 'Aggiorno...' : 'Refresh'}
        </button>
      </div>

      {/* Engine toggle (AI Legacy vs SetScore Paper) */}
      <div data-testid="engine-toggle-tabs"
           className="flex gap-1.5 p-1 rounded-sm"
           style={{ background: '#0F0F11', border: '1px solid #27272A' }}>
        {ENGINES.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            data-testid={`engine-toggle-${key}`}
            onClick={() => setEngine(key)}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-sm text-sm font-medium transition-all ${
              engine === key
                ? key === 'setscore'
                  ? 'bg-violet-500/15 text-violet-300 border border-violet-500/40'
                  : key === 'compare'
                  ? 'bg-cyan-500/15 text-cyan-300 border border-cyan-500/40'
                  : 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                : 'text-zinc-500 hover:text-zinc-300 border border-transparent'
            }`}
          >
            <Icon size={13} />
            {label}
            {key === 'setscore' && (
              <span className="ml-1 text-[9px] uppercase tracking-wider text-violet-400/80">
                paper
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Market Filter */}
      <div data-testid="market-filter-tabs" className="flex gap-1.5 p-1 rounded-sm" style={{ background: '#18181B' }}>
        {MARKETS.map(m => (
          <button
            key={m.key}
            data-testid={`pick-filter-${m.key}`}
            onClick={() => setMarket(m.key)}
            className={`px-4 py-1.5 rounded-sm text-sm font-medium transition-all ${
              market === m.key
                ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                : 'text-zinc-500 hover:text-zinc-300 border border-transparent'
            }`}
          >
            {m.label}
          </button>
        ))}
        <span className="ml-auto flex items-center text-xs text-zinc-600 pr-2">
          {totalAvailable} segnali
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="animate-spin text-zinc-500" size={24} />
        </div>
      ) : engine === 'compare' ? (
        <CompareView
          legacy={compareLegacy}
          setscore={compareSetscore}
        />
      ) : picks.length === 0 ? (
        <div className="text-center py-16 text-zinc-500" data-testid="empty-state">
          <Shield size={32} className="mx-auto mb-3 opacity-40" />
          {engine === 'setscore' ? (
            <>
              <p>Nessun segnale SetScore EV+ disponibile per questo mercato.</p>
              <p className="text-[11px] text-zinc-600 mt-2">
                Il SetScore Engine richiede prediction recente + quote fresh
                (≤ 10 min) + EV ≥ 5% su mercati MW / HC -1.5 / O/U.
              </p>
            </>
          ) : (
            <p>Nessun segnale EV+ disponibile per questo mercato</p>
          )}
        </div>
      ) : (
        <>
          {/* Top 3 Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {topThree.map((p, i) => (
              <Link
                key={`${p.match_id}-${p.selection}-${i}`}
                to={`/tt/matches/${p.match_id}`}
                data-testid={`top-pick-card-${i}`}
                className="block rounded-sm p-4 transition-all hover:translate-y-[-2px]"
                style={{
                  background: i === 0 ? 'linear-gradient(135deg, rgba(16,185,129,0.12) 0%, rgba(9,9,11,1) 100%)' :
                    'rgba(24,24,27,0.8)',
                  border: i === 0 ? '1px solid rgba(16,185,129,0.3)' : '1px solid #27272A',
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {i === 0 && <Trophy size={14} className="text-emerald-400" />}
                    <span className="text-xs font-bold text-zinc-500">#{i + 1}</span>
                    <MarketBadge type={p.market_type} />
                    {p.engine === 'setscore' && (
                      <PaperBadge settleable={p.settleable !== false} />
                    )}
                    {p.confidence_level && <ConfidenceBadge level={p.confidence_level} />}
                    {p.is_stale && (
                      <span data-testid="stale-badge"
                            className="text-[9px] uppercase text-rose-400 tracking-wider">
                        stale
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-zinc-600">
                    <Clock size={10} />
                    {formatTime(p.match_datetime)}
                  </div>
                </div>

                <div className="text-xs text-zinc-500 mb-1 truncate">{p.tournament}</div>
                <div className="text-sm text-zinc-200 font-medium mb-2 truncate">
                  {p.player1} vs {p.player2}
                </div>

                <div className="rounded-sm px-3 py-2 mb-3" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)' }}>
                  <div className="text-emerald-400 font-semibold text-sm truncate">{p.selection}</div>
                  <div className="text-emerald-300/70 text-xs mt-0.5">
                    Quota {Number(p.bookmaker_odds || 0).toFixed(2)}
                    {p.bookmaker_name && (
                      <span className="text-zinc-600"> · {p.bookmaker_name}</span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                  <div>
                    <span className="text-zinc-600">Prob</span>
                    <SafetyMeter prob={p.model_probability || 0} />
                  </div>
                  <div>
                    <span className="text-zinc-600">EV</span>
                    <span className="block text-emerald-400 font-mono text-sm">
                      +{Number(p.ev_pct || 0).toFixed(1)}%
                    </span>
                    {p.ev_lower_pct !== undefined && p.ev_lower_pct !== null && (
                      <span className="block text-zinc-500 text-[10px] font-mono">
                        cons {Number(p.ev_lower_pct).toFixed(1)}%
                      </span>
                    )}
                  </div>
                  <div>
                    <span className="text-zinc-600">Conf.</span>
                    <SafetyMeter prob={p.confidence || 0} />
                  </div>
                  <div>
                    <span className="text-zinc-600">Stake</span>
                    <span className="block text-zinc-300 font-mono">
                      {Number(p.recommended_stake || 0).toFixed(2)}u
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Rest Table */}
          {rest.length > 0 && (
            <div className="rounded-sm overflow-hidden" style={{ border: '1px solid #27272A' }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: '#18181B' }}>
                    <th className="text-left px-3 py-2 text-zinc-500 font-medium text-xs">#</th>
                    <th className="text-left px-3 py-2 text-zinc-500 font-medium text-xs">Ora</th>
                    <th className="text-left px-3 py-2 text-zinc-500 font-medium text-xs">Match</th>
                    <th className="text-left px-3 py-2 text-zinc-500 font-medium text-xs">Pick</th>
                    <th className="text-center px-3 py-2 text-zinc-500 font-medium text-xs">Tipo</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium text-xs">Quota</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium text-xs">Prob</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium text-xs">Conf.</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium text-xs">EV</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium text-xs">Stake</th>
                  </tr>
                </thead>
                <tbody>
                  {rest.map((p, i) => (
                    <tr
                      key={`${p.match_id}-${p.selection}-${i}`}
                      data-testid={`pick-row-${i + 3}`}
                      className="border-t border-zinc-800/50 hover:bg-zinc-800/30 transition-colors cursor-pointer"
                      onClick={() => window.location.href = `/tt/matches/${p.match_id}`}
                    >
                      <td className="px-3 py-2 text-zinc-600 font-mono text-xs">{i + 4}</td>
                      <td className="px-3 py-2 text-zinc-400 font-mono text-xs whitespace-nowrap">
                        {formatDate(p.match_datetime)} {formatTime(p.match_datetime)}
                      </td>
                      <td className="px-3 py-2 text-zinc-300 truncate max-w-[200px]">
                        {p.player1} vs {p.player2}
                      </td>
                      <td className="px-3 py-2 text-emerald-400 font-medium truncate max-w-[180px]">{p.selection}</td>
                      <td className="px-3 py-2 text-center">
                        <div className="inline-flex items-center gap-1">
                          <MarketBadge type={p.market_type} />
                          {p.engine === 'setscore' && (
                            <PaperBadge settleable={p.settleable !== false} />
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right text-zinc-200 font-mono">
                        {Number(p.bookmaker_odds || 0).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className="font-mono text-xs" style={{ color: p.model_probability >= 0.65 ? '#10B981' : p.model_probability >= 0.55 ? '#F59E0B' : '#EF4444' }}>
                          {(Number(p.model_probability || 0) * 100).toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {p.confidence_level && <ConfidenceBadge level={p.confidence_level} />}
                          <span className="font-mono text-xs" style={{ color: p.confidence >= 0.65 ? '#10B981' : p.confidence >= 0.40 ? '#F59E0B' : '#EF4444' }}>
                            {(Number(p.confidence || 0) * 100).toFixed(0)}%
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right text-emerald-400 font-mono">
                        +{Number(p.ev_pct || 0).toFixed(1)}%
                      </td>
                      <td className="px-3 py-2 text-right text-zinc-300 font-mono">
                        {Number(p.recommended_stake || 0).toFixed(2)}u
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================
// Compare view — side-by-side AI Legacy vs SetScore per match.
// Groups picks by match_id, classifies each match into:
//   - CONSENSUS    : both engines pick the SAME side (color: emerald)
//   - DIVERGE      : both engines have a pick but on OPPOSITE sides
//                    (color: amber → audit candidate)
//   - LEGACY_ONLY  : only AI Legacy has a pick (color: zinc)
//   - SETSCORE_ONLY: only SetScore has a pick (color: violet)
//
// We compute consensus by comparing the `selection` field per match. For
// MW we expect player names; for HC -1.5 the SetScore label is
// "<name> -1.5", legacy uses the same convention. For O/U both engines
// use "Over X.5" / "Under X.5".
// ============================================================
function compareKey(p) {
  // Normalize the selection text + market type into a comparable key.
  if (!p) return '';
  const m = (p.market_type || '').toLowerCase();
  const sel = (p.selection || '').trim().toLowerCase();
  if (m === 'totals' || m === 'over_under') return `ou:${sel}`;
  if (m === 'handicap') return `hc:${sel}`;
  return `mw:${sel}`;
}

function classifyMatch(legacyPick, setscorePick) {
  if (legacyPick && setscorePick) {
    return compareKey(legacyPick) === compareKey(setscorePick)
      ? 'CONSENSUS' : 'DIVERGE';
  }
  if (legacyPick) return 'LEGACY_ONLY';
  return 'SETSCORE_ONLY';
}

function groupComparePicks(legacy, setscore) {
  // For each match_id, take the BEST (highest EV) pick per engine.
  const bestPerMatch = (rows) => {
    const map = new Map();
    for (const r of rows) {
      const cur = map.get(r.match_id);
      if (!cur || (r.ev_pct || 0) > (cur.ev_pct || 0)) {
        map.set(r.match_id, r);
      }
    }
    return map;
  };
  const legacyMap = bestPerMatch(legacy);
  const setscoreMap = bestPerMatch(setscore);
  const allIds = new Set([...legacyMap.keys(), ...setscoreMap.keys()]);
  const rows = [];
  for (const mid of allIds) {
    const lp = legacyMap.get(mid) || null;
    const sp = setscoreMap.get(mid) || null;
    rows.push({
      match_id: mid,
      match_label: (lp || sp).match_datetime
        ? `${(lp || sp).player1} vs ${(lp || sp).player2}`
        : mid,
      tournament: (lp || sp).tournament,
      match_datetime: (lp || sp).match_datetime,
      legacy: lp,
      setscore: sp,
      status: classifyMatch(lp, sp),
    });
  }
  // Sort: CONSENSUS first (highest combined EV), then DIVERGE,
  // then SETSCORE_ONLY, then LEGACY_ONLY.
  const order = { CONSENSUS: 0, DIVERGE: 1, SETSCORE_ONLY: 2, LEGACY_ONLY: 3 };
  rows.sort((a, b) => {
    const da = order[a.status] - order[b.status];
    if (da !== 0) return da;
    const evA = (a.legacy?.ev_pct || 0) + (a.setscore?.ev_pct || 0);
    const evB = (b.legacy?.ev_pct || 0) + (b.setscore?.ev_pct || 0);
    return evB - evA;
  });
  return rows;
}

function StatusBadge({ status }) {
  const cfg = {
    CONSENSUS:     { label: 'CONSENSUS',    cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' },
    DIVERGE:       { label: 'DIVERGE',      cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40' },
    LEGACY_ONLY:   { label: 'LEGACY ONLY',  cls: 'bg-zinc-700 text-zinc-300 border-zinc-600' },
    SETSCORE_ONLY: { label: 'SETSCORE ONLY', cls: 'bg-violet-500/15 text-violet-300 border-violet-500/40' },
  }[status] || { label: status, cls: 'bg-zinc-700 text-zinc-300' };
  return (
    <span data-testid={`status-${status}`}
          className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function PickCell({ pick, accent }) {
  if (!pick) {
    return <span className="text-zinc-700 text-xs">—</span>;
  }
  const evColor = (pick.ev_pct || 0) >= 10 ? 'text-emerald-300 font-semibold'
    : (pick.ev_pct || 0) >= 5 ? 'text-emerald-400'
    : 'text-zinc-400';
  return (
    <Link to={`/tt/matches/${pick.match_id}`}
          data-testid={`compare-pick-${pick.engine || 'legacy'}-${pick.match_id}`}
          className="block hover:bg-zinc-900/50 rounded-sm px-2 py-1.5 transition-colors">
      <div className="flex items-center gap-1.5 mb-0.5">
        <MarketBadge type={pick.market_type} />
        <span className={`text-xs font-medium ${accent}`}>{pick.selection}</span>
      </div>
      <div className="text-[10px] text-zinc-500 font-mono flex items-center gap-2">
        <span>@ {Number(pick.bookmaker_odds || 0).toFixed(2)}</span>
        <span className={evColor}>+{Number(pick.ev_pct || 0).toFixed(1)}%</span>
        {pick.ev_lower_pct !== undefined && pick.ev_lower_pct !== null && (
          <span className="text-zinc-600">cons {Number(pick.ev_lower_pct).toFixed(1)}%</span>
        )}
        {pick.is_stale && <span className="text-rose-400">stale</span>}
      </div>
    </Link>
  );
}

function CompareView({ legacy, setscore }) {
  const rows = useMemo(
    () => groupComparePicks(legacy, setscore),
    [legacy, setscore],
  );
  const counts = useMemo(() => ({
    consensus: rows.filter(r => r.status === 'CONSENSUS').length,
    diverge: rows.filter(r => r.status === 'DIVERGE').length,
    legacyOnly: rows.filter(r => r.status === 'LEGACY_ONLY').length,
    setscoreOnly: rows.filter(r => r.status === 'SETSCORE_ONLY').length,
  }), [rows]);

  if (rows.length === 0) {
    return (
      <div className="text-center py-16 text-zinc-500"
           data-testid="compare-empty">
        <GitCompare size={32} className="mx-auto mb-3 opacity-40" />
        <p>Nessun segnale disponibile in nessuno dei due engine.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="compare-view">
      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="rounded-sm p-3 border border-emerald-500/30 bg-emerald-500/5"
             data-testid="compare-summary-consensus">
          <div className="text-[10px] uppercase text-emerald-400 tracking-wider">Consensus</div>
          <div className="text-2xl font-bold text-emerald-300 font-mono">{counts.consensus}</div>
          <div className="text-[10px] text-zinc-500">stesso side, alta convinzione</div>
        </div>
        <div className="rounded-sm p-3 border border-amber-500/30 bg-amber-500/5"
             data-testid="compare-summary-diverge">
          <div className="text-[10px] uppercase text-amber-400 tracking-wider">Diverge</div>
          <div className="text-2xl font-bold text-amber-300 font-mono">{counts.diverge}</div>
          <div className="text-[10px] text-zinc-500">side opposti, audit candidate</div>
        </div>
        <div className="rounded-sm p-3 border border-zinc-700 bg-zinc-900/40"
             data-testid="compare-summary-legacy-only">
          <div className="text-[10px] uppercase text-zinc-400 tracking-wider">Legacy only</div>
          <div className="text-2xl font-bold text-zinc-300 font-mono">{counts.legacyOnly}</div>
          <div className="text-[10px] text-zinc-600">SetScore non ha trovato EV+</div>
        </div>
        <div className="rounded-sm p-3 border border-violet-500/30 bg-violet-500/5"
             data-testid="compare-summary-setscore-only">
          <div className="text-[10px] uppercase text-violet-400 tracking-wider">SetScore only</div>
          <div className="text-2xl font-bold text-violet-300 font-mono">{counts.setscoreOnly}</div>
          <div className="text-[10px] text-zinc-500">Legacy non ha trovato EV+</div>
        </div>
      </div>

      {/* Comparison table */}
      <div className="rounded-sm overflow-hidden border border-zinc-800">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: '#18181B' }}>
              <th className="text-left px-3 py-2 text-zinc-500 font-medium text-xs">Status</th>
              <th className="text-left px-3 py-2 text-zinc-500 font-medium text-xs">Match</th>
              <th className="text-left px-3 py-2 text-zinc-500 font-medium text-xs">
                <div className="flex items-center gap-1">
                  <Cpu size={11} className="text-emerald-400" />
                  AI Legacy
                </div>
              </th>
              <th className="text-left px-3 py-2 text-zinc-500 font-medium text-xs">
                <div className="flex items-center gap-1">
                  <Brain size={11} className="text-violet-400" />
                  SetScore (paper)
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.match_id}
                  data-testid={`compare-row-${r.match_id}`}
                  className="border-t border-zinc-800/50 hover:bg-zinc-900/30 transition-colors"
                  style={{
                    background: r.status === 'CONSENSUS' ? 'rgba(16,185,129,0.04)'
                      : r.status === 'DIVERGE' ? 'rgba(245,158,11,0.04)'
                      : 'transparent',
                  }}>
                <td className="px-3 py-2 align-top">
                  <StatusBadge status={r.status} />
                </td>
                <td className="px-3 py-2 align-top">
                  <div className="text-zinc-300 truncate max-w-[220px]">{r.match_label}</div>
                  <div className="text-[10px] text-zinc-600 font-mono">
                    {r.tournament} · {formatDate(r.match_datetime)} {formatTime(r.match_datetime)}
                  </div>
                </td>
                <td className="px-3 py-2 align-top w-[300px]">
                  <PickCell pick={r.legacy} accent="text-emerald-300" />
                </td>
                <td className="px-3 py-2 align-top w-[300px]">
                  <PickCell pick={r.setscore} accent="text-violet-300" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-sm p-3 text-[10px] text-zinc-500 leading-relaxed border border-zinc-800/50">
        <span className="text-emerald-400 font-semibold">CONSENSUS</span>: entrambi
        gli engine puntano sullo stesso side con EV ≥ 5%. Massima convinzione.
        <span className="mx-2 text-zinc-700">·</span>
        <span className="text-amber-400 font-semibold">DIVERGE</span>: i due engine
        puntano su side opposti. Audit per capire chi sbaglia.
        <span className="mx-2 text-zinc-700">·</span>
        SetScore opera in <span className="text-violet-300">PAPER_SIGNAL</span> only.
      </div>
    </div>
  );
}
